import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlaceIcon from '@mui/icons-material/Place';
import StoreIcon from '@mui/icons-material/Store';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import axiosInstance from '../../lib/axios';

type FilterType = 'all' | 'store' | 'warehouse';

const fetchLocations = async (page: number, pageSize: number, filter: FilterType) => {
  let endpoint = '/locations/admin/all';
  if (filter === 'store') {
    endpoint = '/locations/admin/stores';
  } else if (filter === 'warehouse') {
    endpoint = '/locations/admin/warehouses';
  }

  const response = await axiosInstance.get(endpoint, {
    params: {
      page: page + 1,
      limit: pageSize,
    },
  });

  return {
    data: Array.isArray(response.data) ? response.data : response.data.data || [],
    total: response.data.total || (Array.isArray(response.data) ? response.data.length : 0),
  };
};

export const LocationList = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('all');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['locations', paginationModel.page, paginationModel.pageSize, filter],
    queryFn: () => fetchLocations(paginationModel.page, paginationModel.pageSize, filter),
  });

  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: FilterType | null) => {
    if (newFilter !== null) {
      setFilter(newFilter);
      setPaginationModel({ ...paginationModel, page: 0 });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Location Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={params.value === 'store' ? <StoreIcon fontSize="small" /> : <WarehouseIcon fontSize="small" />}
          label={params.value === 'store' ? 'Store' : 'Warehouse'}
          size="small"
          color={params.value === 'store' ? 'primary' : 'secondary'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'organizationName',
      headerName: 'Organization',
      flex: 1,
      minWidth: 200,
      valueGetter: (params: any) => params.row.organization?.name || 'N/A',
    },
    {
      field: 'city',
      headerName: 'City',
      width: 150,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
    },
    {
      field: 'capabilities',
      headerName: 'Capabilities',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const capabilities: string[] = [];
        if (params.row.canSellToCustomers) capabilities.push('Sell');
        if (params.row.canFulfillOnlineOrders) capabilities.push('Fulfill');
        if (params.row.canReceiveTransfers) capabilities.push('Receive');
        if (params.row.canSendTransfers) capabilities.push('Send');
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {capabilities.map((cap) => (
              <Chip key={cap} label={cap} size="small" variant="outlined" />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => navigate(`/locations/show/${params.row.id}`)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => navigate(`/locations/edit/${params.row.id}`)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PlaceIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4">Locations</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/locations/create')}
        >
          Create Location
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={handleFilterChange}
          size="small"
        >
          <ToggleButton value="all">
            <PlaceIcon sx={{ mr: 1 }} />
            All
          </ToggleButton>
          <ToggleButton value="store">
            <StoreIcon sx={{ mr: 1 }} />
            Stores
          </ToggleButton>
          <ToggleButton value="warehouse">
            <WarehouseIcon sx={{ mr: 1 }} />
            Warehouses
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Card>
        <CardContent>
          <DataGrid
            rows={data?.data || []}
            columns={columns}
            rowCount={data?.total || 0}
            loading={isLoading}
            pageSizeOptions={[5, 10, 25, 50]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            autoHeight
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>
    </Box>
  );
};
